import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { resolutionsService, Resolution, getErrorMessage } from '@/services';

// ============================================================================
// Validation
// ============================================================================

const resolutionSchema = z.object({
  name: z.string().min(1, 'validation.nameRequired'),
  xaccel_value: z.string().min(1, 'validation.xaccelValueRequired'),
});

type ResolutionForm = z.infer<typeof resolutionSchema>;

// ============================================================================
// Sortable Card
// ============================================================================

interface SortableResolutionCardProps {
  resolution: Resolution;
  competitionCount: number;
  deleteConfirm: number | null;
  onEdit: (res: Resolution) => void;
  onDeleteConfirm: (id: number) => void;
  onDeleteCancel: () => void;
  onDelete: (id: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

function SortableResolutionCard({
  resolution, competitionCount, deleteConfirm, onEdit, onDeleteConfirm, onDeleteCancel, onDelete, t,
}: SortableResolutionCardProps) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: resolution.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-dark-700 text-dark-500 hover:text-dark-300"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-dark-100">{resolution.name}</h3>
              <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-dark-700 text-dark-400">
                {resolution.xaccel_value}
              </span>
            </div>
            {competitionCount > 0 && (
              <p className="text-dark-500 text-xs mt-1">
                {t('resolutions.usedIn', { count: competitionCount })}
              </p>
            )}
          </div>
        </div>
        {deleteConfirm === resolution.id ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(resolution.id)}
              className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
            >
              <Check size={16} />
            </button>
            <button
              onClick={onDeleteCancel}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(resolution)}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => onDeleteConfirm(resolution.id)}
              className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function Resolutions() {
  const { t } = useTranslation();
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [competitionCounts, setCompetitionCounts] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingResolution, setEditingResolution] = useState<Resolution | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ResolutionForm>({
    resolver: zodResolver(resolutionSchema),
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const fetchResolutions = async () => {
    try {
      const data = await resolutionsService.list();
      setResolutions(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompetitionCounts = async () => {
    try {
      const { competitionsService } = await import('@/services');
      const competitions = await competitionsService.list();
      const counts: Record<number, number> = {};
      for (const comp of competitions) {
        if (comp.resolutions) {
          for (const r of comp.resolutions) {
            counts[r.id] = (counts[r.id] || 0) + 1;
          }
        }
      }
      setCompetitionCounts(counts);
    } catch {
      // Non-critical, just won't show counts
    }
  };

  useEffect(() => {
    fetchResolutions();
    fetchCompetitionCounts();
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = resolutions.findIndex(r => r.id === active.id);
    const newIndex = resolutions.findIndex(r => r.id === over.id);
    const reordered = arrayMove(resolutions, oldIndex, newIndex);

    setResolutions(reordered);

    try {
      await resolutionsService.reorder(reordered.map(r => r.id));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('resolutions.reorderError'));
      fetchResolutions();
    }
  };

  const openCreateModal = () => {
    setEditingResolution(null);
    reset({ name: '', xaccel_value: '' });
    setShowModal(true);
  };

  const openEditModal = (resolution: Resolution) => {
    setEditingResolution(resolution);
    reset({
      name: resolution.name,
      xaccel_value: resolution.xaccel_value,
    });
    setShowModal(true);
  };

  const onSubmit = async (data: ResolutionForm) => {
    try {
      if (editingResolution) {
        await resolutionsService.update(editingResolution.id, data);
        toast.success(t('resolutions.updated'));
      } else {
        await resolutionsService.create(data);
        toast.success(t('resolutions.created'));
      }
      setShowModal(false);
      fetchResolutions();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await resolutionsService.delete(id);
      toast.success(t('resolutions.deleted'));
      setDeleteConfirm(null);
      fetchResolutions();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('resolutions.title')}</h1>
          <p className="text-dark-400 mt-1">
            {resolutions.length} {t('resolutions.configuredCount')}
          </p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          <Plus size={18} />
          {t('common.add')}
        </button>
      </div>

      {/* Cards with DnD */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-16 bg-dark-700 rounded" />
            </div>
          ))}
        </div>
      ) : resolutions.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('resolutions.noData')}</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={resolutions.map(r => r.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resolutions.map((resolution) => (
                <SortableResolutionCard
                  key={resolution.id}
                  resolution={resolution}
                  competitionCount={competitionCounts[resolution.id] || 0}
                  deleteConfirm={deleteConfirm}
                  onEdit={openEditModal}
                  onDeleteConfirm={(id) => setDeleteConfirm(id)}
                  onDeleteCancel={() => setDeleteConfirm(null)}
                  onDelete={handleDelete}
                  t={t}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-md m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingResolution ? t('resolutions.edit') : t('resolutions.new')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">{t('resolutions.nameLabel')} *</label>
                <input
                  type="text"
                  className={errors.name ? 'input-error' : 'input'}
                  placeholder={t('resolutions.namePlaceholder')}
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-500">{t(errors.name.message || '')}</p>
                )}
                <p className="text-xs text-dark-500 mt-1">
                  {t('resolutions.usedAsSuffix')}: SERIEA_01_(<strong>FHD</strong>)
                </p>
              </div>

              <div>
                <label className="label">{t('resolutions.xaccelValue')} *</label>
                <input
                  type="text"
                  className={errors.xaccel_value ? 'input-error' : 'input'}
                  placeholder={t('resolutions.xaccelPlaceholder')}
                  {...register('xaccel_value')}
                />
                {errors.xaccel_value && (
                  <p className="mt-1 text-sm text-error-500">{t(errors.xaccel_value.message || '')}</p>
                )}
                <p className="text-xs text-dark-500 mt-1">
                  {t('resolutions.xaccelHelp')}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
